import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withViewTransitions,
} from '@angular/router';
import { InMemoryCache, split } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { ConfirmationService, MessageService } from 'primeng/api';
import { delay } from 'rxjs';
import {
  LocalStorageJwtService,
  ThemeStore,
  tokenInterceptor,
} from '~/shared-common';
import { ngrxLocalFeature } from '~/shared-core/data-access';
import { errorHandlingInterceptor } from '~/shared-core/error-handler';
import { ngrxFormsEffects, ngrxFormsFeature } from '~/shared-core/forms';
import { API_URL, NOTI_API_URL, NOTI_WS_URL } from '~/shared-core/http-client';
import { ENABLED_LOGGER } from '~/shared-core/logger';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { stylerAuthInterceptor } from './styler-auth/styler-auth.interceptor';

export const appConfig: ApplicationConfig = {
  providers: [
    provideClientHydration(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    MessageService,
    ConfirmationService,
    provideRouter(
      appRoutes,
      withViewTransitions({
        onViewTransitionCreated: (transitionInfo) => {
          const themeStore = inject(ThemeStore);
          transitionInfo.transition.ready.then(() => {
            themeStore.setLoading(true);
          });
          transitionInfo.transition.finished.then(() => {
            themeStore.setLoading(false);
          });
        },
      }),
      withComponentInputBinding(),
    ),
    {
      provide: API_URL,
      useValue: environment.api_url,
    },
    {
      provide: NOTI_API_URL,
      useValue: environment.noti_api_url,
    },
    {
      provide: NOTI_WS_URL,
      useValue: environment.noti_ws_url,
    },
    {
      provide: ENABLED_LOGGER,
      useValue: !environment.production,
    },
    provideStore({
      router: routerReducer,
      ngrxForms: ngrxFormsFeature.reducer,
      ngrxLocal: ngrxLocalFeature.reducer,
    }),
    provideEffects(ngrxFormsEffects),
    provideRouterStore(),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        errorHandlingInterceptor,
        tokenInterceptor,
        stylerAuthInterceptor,
      ]),
    ),
    // provideApiService(),
    provideNamedApollo(() => {
      const httpLink = inject(HttpLink);
      const apiUrl = inject(API_URL);
      const localStorageJwtService = inject(LocalStorageJwtService);
      const notiApiUrl = inject(NOTI_API_URL);
      const notiWsUrl = inject(NOTI_WS_URL);
      const wsLink =
        typeof window !== 'undefined'
          ? new GraphQLWsLink(
              createClient({
                url: notiWsUrl,
                lazy: true,
                connectionParams: () =>
                  new Promise<{ authToken: string | null }>((resolve) => {
                    localStorageJwtService
                      .getItem()
                      .pipe(delay(300))
                      .subscribe((authToken: string | null) => {
                        resolve({
                          authToken,
                        });
                      });
                  }),
              }),
            )
          : null;
      const link =
        typeof window !== 'undefined' && wsLink !== null
          ? split(
              ({ query }: { query: any }) => {
                const definition = getMainDefinition(query);
                return (
                  definition.kind === Kind.OPERATION_DEFINITION &&
                  definition.operation === OperationTypeNode.SUBSCRIPTION
                );
              },
              wsLink,
              httpLink.create({
                uri: notiApiUrl,
              }),
            )
          : httpLink.create({
              uri: notiApiUrl,
            });
      return {
        default: {
          link:
            typeof window !== 'undefined'
              ? split(
                  ({ query }: { query: any }) => {
                    const definition = getMainDefinition(query);
                    return (
                      definition.kind === Kind.OPERATION_DEFINITION &&
                      definition.operation === OperationTypeNode.SUBSCRIPTION
                    );
                  },
                  new GraphQLWsLink(
                    createClient({
                      url: apiUrl,
                      lazy: true,
                      connectionParams: () =>
                        new Promise<{ authToken: string | null }>((resolve) => {
                          localStorageJwtService
                            .getItem()
                            .pipe(delay(300))
                            .subscribe((authToken: string | null) => {
                              resolve({
                                authToken,
                              });
                            });
                        }),
                    }),
                  ),
                  httpLink.create({
                    uri: apiUrl,
                  }),
                )
              : httpLink.create({
                  uri: apiUrl,
                }),
          cache: new InMemoryCache({
            addTypename: false,
          }),
        },
        notiGraphQl: {
          link,
          cache: new InMemoryCache({
            addTypename: false,
          }),
        },
      };
    }),
    provideAnimationsAsync(),
    !environment.production ? provideStoreDevtools() : [],
  ],
};
